import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AlertCircle, Lock, Unlock } from 'lucide-react';
import {
  useICalFeedEventsBySource,
  useReleaseICalEvent,
  useReblockICalEvent,
} from '@/hooks/useICalFeed.ts';
import { formatDate } from '@/utils/formatDate.ts';
import { ICalEventType, ICalFeedSourceKey, ReleaseState } from '@/types/icalFeed.ts';
import { IcalActionErrorCode } from '@/services/icalFeedService.ts';

interface ICalEventsTableProps {
  readonly apartmentId: string;
  readonly icalFeedConfigurationId?: string;
  readonly sourceKey?: ICalFeedSourceKey;
}

/** Format a known event type for human-readable display. */
const formatEventType = (eventType: ICalEventType | undefined, t: TFunction<readonly ['pages', 'errors'], undefined>): string => {
  if (!eventType) return '—';
  if (eventType === 'AIRBNB_BLOCK') return 'Airbnb block';
  if (eventType === 'BOOKING') return t('pages:eventTypeExternalReservation');
  if (eventType === 'BLOCKED') return t('pages:eventTypeExternalBlock');
  return String(eventType);
};

const formatReleaseState = (releaseState: ReleaseState | undefined, t: TFunction<readonly ['pages', 'errors'], undefined>): string => {
  if (!releaseState) return '—';
  if (releaseState === 'BLOCKED') return t('pages:releaseStateBlocked');
  if (releaseState === 'RELEASED') return t('pages:releaseStateReleased');
  return String(releaseState);
};

/** Maps a release/reblock action error code to a distinct, owner-facing message. */
const errorMessageForCode = (code: IcalActionErrorCode, t: TFunction<readonly ['pages', 'errors'], undefined>): string => {
  switch (code) {
    case 'CONFIRMED_BOOKING_NOT_RELEASABLE':
      return t('errors:icalConfirmedBookingNotReleasable');
    case 'ALREADY_RELEASED':
      return t('errors:icalAlreadyReleased');
    case 'NOT_RELEASED':
      return t('errors:icalNotReleased');
    case 'PLATFORM_BOOKING_EXISTS':
      return t('errors:icalPlatformBookingExists');
    case 'NOT_FOUND':
      return t('errors:icalEventNotFound');
    default:
      return t('errors:icalActionFailed');
  }
};

/**
 * ICalEventsTable — renders the list of iCal events imported from a configured feed.
 *
 * Covers US4 (view), and 036's US1 (event-type/release-state disambiguation),
 * US2 (release an imported external block), US3 (re-block + conflict triage).
 *
 * Graceful degradation: renders "—" for any absent field; never throws.
 * Uses `icalUid ?? id ?? String(index)` as the React list key.
 */
export const ICalEventsTable = ({
  apartmentId,
  icalFeedConfigurationId,
  sourceKey,
}: ICalEventsTableProps) => {
  const { t } = useTranslation(['pages', 'errors']);
  const [includeDisappeared, setIncludeDisappeared] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState<'BOOKING' | 'BLOCKED' | ''>('');
  const [releaseStateFilter, setReleaseStateFilter] = useState<ReleaseState | ''>('');
  const [conflictOnly, setConflictOnly] = useState(false);
  const [actionErrorByEventId, setActionErrorByEventId] = useState<Record<string, string>>({});

  const { data: events, isLoading, isError } = useICalFeedEventsBySource(apartmentId, {
    includeDisappeared,
    icalFeedConfigurationId,
    sourceKey,
    eventType: eventTypeFilter || undefined,
    releaseState: releaseStateFilter || undefined,
    conflict: conflictOnly || undefined,
  });

  const releaseMutation = useReleaseICalEvent();
  const reblockMutation = useReblockICalEvent();

  const handleRelease = (eventId: string) => {
    setActionErrorByEventId(current => ({ ...current, [eventId]: '' }));
    releaseMutation.mutate(
      { apartmentId, eventId },
      {
        onError: err => {
          const code = (err as { code?: IcalActionErrorCode })?.code ?? 'UNKNOWN';
          setActionErrorByEventId(current => ({
            ...current,
            [eventId]: errorMessageForCode(code, t),
          }));
        },
      }
    );
  };

  const handleReblock = (eventId: string) => {
    setActionErrorByEventId(current => ({ ...current, [eventId]: '' }));
    reblockMutation.mutate(
      { apartmentId, eventId },
      {
        onError: err => {
          const code = (err as { code?: IcalActionErrorCode })?.code ?? 'UNKNOWN';
          setActionErrorByEventId(current => ({
            ...current,
            [eventId]: errorMessageForCode(code, t),
          }));
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="mt-4">
        <div className="h-3 bg-gray-200 rounded animate-pulse w-1/4 mb-2" />
        <div className="h-3 bg-gray-200 rounded animate-pulse w-full mb-1" />
        <div className="h-3 bg-gray-200 rounded animate-pulse w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mt-4 p-3 border border-red-200 rounded-lg bg-red-50 flex items-center gap-2 text-red-700 text-xs">
        <AlertCircle size={14} />
        {t('errors:failedToLoadICalEvents')}
      </div>
    );
  }

  return (
    <div className="mt-4">
      {/* Heading + filters */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h5 className="text-xs font-semibold text-gray-700">{t('pages:icalEventsHeading')}</h5>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeDisappeared}
              onChange={e => setIncludeDisappeared(e.target.checked)}
              className="h-3 w-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            {t('pages:showDisappearedEvents')}
          </label>

          <select
            aria-label={t('pages:columnEventType')}
            value={eventTypeFilter}
            onChange={e => setEventTypeFilter(e.target.value as 'BOOKING' | 'BLOCKED' | '')}
            className="text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white"
          >
            <option value="">{t('pages:icalFilterAllEventTypes')}</option>
            <option value="BOOKING">{t('pages:eventTypeExternalReservation')}</option>
            <option value="BLOCKED">{t('pages:eventTypeExternalBlock')}</option>
          </select>

          <select
            aria-label={t('pages:columnReleaseState')}
            value={releaseStateFilter}
            onChange={e => setReleaseStateFilter(e.target.value as ReleaseState | '')}
            className="text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white"
          >
            <option value="">{t('pages:icalFilterAllReleaseStates')}</option>
            <option value="BLOCKED">{t('pages:releaseStateBlocked')}</option>
            <option value="RELEASED">{t('pages:releaseStateReleased')}</option>
          </select>

          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={conflictOnly}
              onChange={e => setConflictOnly(e.target.checked)}
              className="h-3 w-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            {t('pages:icalConflictFilterLabel')}
          </label>
        </div>
      </div>

      {/* Empty state */}
      {(!events || events.length === 0) ? (
        <p className="text-xs text-gray-500 italic">{t('pages:noICalEvents')}</p>
      ) : (
        /* Outer wrapper enables horizontal scroll; inner div caps height to ~10 rows */
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-100 bg-white sticky top-0">
                <th className="pb-1 pr-3 font-medium">{t('pages:columnStartDate')}</th>
                <th className="pb-1 pr-3 font-medium">{t('pages:columnEndDate')}</th>
                <th className="pb-1 pr-3 font-medium">{t('pages:columnSummary')}</th>
                <th className="pb-1 pr-3 font-medium hidden md:table-cell">{t('pages:columnEventType')}</th>
                <th className="pb-1 pr-3 font-medium hidden md:table-cell">{t('pages:columnReleaseState')}</th>
                <th className="pb-1 font-medium hidden md:table-cell">{t('pages:columnDisappeared')}</th>
                <th className="pb-1 font-medium">{t('pages:columnActions')}</th>
              </tr>
            </thead>
          </table>
          {/* Scrollable body — each row is ~28px tall; 10 rows ≈ 280px */}
          <div className="overflow-y-auto max-h-[280px]">
            <table className="w-full text-xs border-collapse">
              <tbody>
                {events.map((event, index) => {
                  const key = event.icalUid ?? event.id ?? String(index);
                  const isDisappeared = !!event.disappearedAt;
                  const isBlockedType = event.eventType === 'BLOCKED';
                  const eventId = event.id ?? '';
                  const canRelease = isBlockedType && event.releaseState !== 'RELEASED';
                  const canReblock = isBlockedType && event.releaseState === 'RELEASED';
                  const hasConflict = !!event.conflictReason;
                  const rowActionError = eventId ? actionErrorByEventId[eventId] : undefined;
                  const isRowMutating =
                    (releaseMutation.isPending && releaseMutation.variables?.eventId === eventId) ||
                    (reblockMutation.isPending && reblockMutation.variables?.eventId === eventId);

                  return (
                    <tr
                      key={key}
                      className={`border-b border-gray-50 ${
                        isDisappeared ? 'text-gray-400 line-through' : 'text-gray-700'
                      }`}
                    >
                      <td className="py-1 pr-3">
                        {event.startDate ? formatDate(event.startDate) : '—'}
                      </td>
                      <td className="py-1 pr-3">
                        {event.endDate ? formatDate(event.endDate) : '—'}
                      </td>
                      <td className="py-1 pr-3 max-w-[200px] truncate">
                        {event.summary ?? '—'}
                      </td>
                      <td className="py-1 pr-3 hidden md:table-cell">
                        {formatEventType(event.eventType, t)}
                      </td>
                      <td className="py-1 pr-3 hidden md:table-cell">
                        {isBlockedType ? formatReleaseState(event.releaseState, t) : '—'}
                      </td>
                      <td className="py-1 hidden md:table-cell">
                        {isDisappeared ? formatDate(event.disappearedAt!.split('T')[0]) : ''}
                      </td>
                      <td className="py-1">
                        <div className="flex items-center gap-1">
                          {canRelease && eventId && (
                            <button
                              type="button"
                              onClick={() => handleRelease(eventId)}
                              disabled={isRowMutating}
                              title={t('pages:releaseAction')}
                              aria-label={`${t('pages:releaseAction')}: ${event.summary ?? eventId}`}
                              className="p-1 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                            >
                              <Unlock size={12} className="text-green-600" />
                            </button>
                          )}
                          {canReblock && eventId && (
                            <button
                              type="button"
                              onClick={() => handleReblock(eventId)}
                              disabled={isRowMutating}
                              title={t('pages:reblockAction')}
                              aria-label={`${t('pages:reblockAction')}: ${event.summary ?? eventId}`}
                              className="p-1 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                            >
                              <Lock size={12} className="text-red-600" />
                            </button>
                          )}
                          {hasConflict && (
                            <span
                              className="px-1.5 py-0.5 text-[10px] rounded-full bg-amber-100 text-amber-800 border border-amber-200"
                              title={
                                event.conflictDetectedAt
                                  ? `${event.conflictReason} (${formatDate(event.conflictDetectedAt.split('T')[0])})`
                                  : event.conflictReason
                              }
                            >
                              {t('pages:icalConflictBadgeLabel')}
                            </span>
                          )}
                        </div>
                        {rowActionError && (
                          <p className="mt-0.5 text-red-600" role="alert">{rowActionError}</p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ICalEventsTable;
